import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import { useAuthenticatedSession } from '../auth/session'
import {
  assignGateway, createBiller, createUser, exportTransactions, getAudit, getBillers, getFieldExposure,
  getGateways, getProfile, getTransactions, getUsers, grantFieldExposure, resetUserPassword,
  revokeFieldExposure, sendSupportRequest, setGatewayLabel, updateBiller, updateUser,
  type CreateBillerInput, type CreateUserInput, type GatewayQuery, type TransactionQuery,
  type UpdateBillerInput, type UpdateUserInput,
  getGatewayBeneficiaries, getBillerSlices, assignSlice, revokeSlice, type SliceInput,
} from './client'
import type {
  AuditEvent, Biller, BillerSlices, FieldExposureResponse, FieldExposureRule, GatewayBeneficiaries, GatewayEntry, GrantFieldExposureInput, Profile, SupportRequestInput, TransactionsResponse, UsersResponse,
} from './types'

/**
 * Query keys all start with 'api' so `SessionProvider.switchBiller` can drop
 * every biller-scoped cache entry in one `invalidateQueries({ queryKey: ['api'] })`.
 * If a key ever escapes that prefix, switching biller will leave the previous
 * tenant's rows on screen under the new tenant's name — visible, and a
 * tenant-isolation bug in appearance even though the server-side scope is
 * correct.
 */
export const apiKeys = {
  all: ['api'] as const,
  transactions: (q: TransactionQuery) => ['api', 'transactions', q] as const,
  billers: () => ['api', 'billers'] as const,
  gateways: (q: GatewayQuery) => ['api', 'gateways', q] as const,
  profile: () => ['api', 'profile'] as const,
  users: () => ['api', 'users'] as const,
  audit: () => ['api', 'audit'] as const,
  fieldExposure: () => ['api', 'field-exposure'] as const,
}

/**
 * The filter set is part of the query key, which is what preserves the
 * out-of-order guard the screen this replaces implemented with a manual
 * request-sequence counter: a late response for filter set A is written to A's
 * cache slot and can never appear under filter set B. `signal` additionally
 * aborts the in-flight request outright, so a superseded query stops costing
 * BigQuery money the moment it is superseded.
 *
 * `placeholderData: keepPreviousData` is the visible half of the same idea:
 * changing a filter dims the existing rows instead of replacing the table with
 * skeletons, so the page does not flash empty for 1-3 seconds on every change.
 */
export function useTransactions(query: TransactionQuery): UseQueryResult<TransactionsResponse> {
  return useQuery({
    queryKey: apiKeys.transactions(query),
    queryFn: ({ signal }) => getTransactions(query, signal),
    placeholderData: (previous) => previous,
  })
}

export const useBillers = (enabled: boolean): UseQueryResult<readonly Biller[]> =>
  useQuery({
    queryKey: apiKeys.billers(),
    queryFn: ({ signal }) => getBillers(signal),
    // Only internal users may call /v1/billers; for anyone else it is a 403,
    // and firing it just to discard the error is noise in the API's audit log.
    enabled,
    staleTime: 5 * 60_000,
  })

/**
 * The gateway catalogue.
 *
 * Enabled for everyone: a biller user gets their own gateways, which is what
 * populates the gateway filter on the transactions screen. The server decides
 * what "their own" means, so this hook does not need to know the caller's
 * role — and cannot get it wrong.
 */
export const useGateways = (filter: GatewayQuery = {}): UseQueryResult<readonly GatewayEntry[]> =>
  useQuery({
    queryKey: apiKeys.gateways(filter),
    queryFn: ({ signal }) => getGateways(filter, signal),
    placeholderData: (previous) => previous,
    staleTime: 60_000,
  })

export const useProfile = (): UseQueryResult<Profile> =>
  useQuery({ queryKey: apiKeys.profile(), queryFn: ({ signal }) => getProfile(signal) })

export const useUsers = (enabled: boolean): UseQueryResult<UsersResponse> =>
  useQuery({ queryKey: apiKeys.users(), queryFn: ({ signal }) => getUsers(signal), enabled })

export const useAudit = (): UseQueryResult<readonly AuditEvent[]> =>
  useQuery({ queryKey: apiKeys.audit(), queryFn: ({ signal }) => getAudit(signal) })

/** Mutations refetch rather than patching the cache by hand. A role change
 *  that renders instantly and then silently reverts is worse than a 400ms
 *  wait, and the API applies authorisation rules the client cannot predict. */
function useUsersMutation<TInput, TResult>(
  run: (input: TInput, csrfToken: string) => Promise<TResult>,
): UseMutationResult<TResult, Error, TInput> {
  const { csrfToken } = useAuthenticatedSession()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: TInput) => run(input, csrfToken),
    onSuccess: () => client.invalidateQueries({ queryKey: apiKeys.users() }),
  })
}

export const useCreateUser = () => useUsersMutation<CreateUserInput, Awaited<ReturnType<typeof createUser>>>(createUser)

export const useUpdateUser = () =>
  useUsersMutation<{ id: string; changes: UpdateUserInput }, Awaited<ReturnType<typeof updateUser>>>(
    ({ id, changes }, csrf) => updateUser(id, changes, csrf),
  )

export const useResetPassword = () =>
  useUsersMutation<string, { sent: boolean }>((id, csrf) => resetUserPassword(id, csrf))

/**
 * Administration mutations invalidate BOTH the biller list and the gateway
 * catalogue, because every one of them can change either: assigning a gateway
 * changes a biller's row, and editing a biller's gateway set changes several
 * catalogue rows. Invalidating only the obvious one leaves the other screen
 * showing a state that no longer exists.
 */
function useAdminMutation<TInput, TResult>(
  run: (input: TInput, csrfToken: string) => Promise<TResult>,
): UseMutationResult<TResult, Error, TInput> {
  const { csrfToken } = useAuthenticatedSession()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: TInput) => run(input, csrfToken),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: apiKeys.billers() })
      await client.invalidateQueries({ queryKey: ['api', 'gateways'] })
      // Scope can change under the current user — deactivating a biller or
      // moving a gateway changes which transactions are theirs to see.
      await client.invalidateQueries({ queryKey: ['api', 'transactions'] })
    },
  })
}

export const useCreateBiller = () =>
  useAdminMutation<CreateBillerInput, Awaited<ReturnType<typeof createBiller>>>(createBiller)

export const useUpdateBiller = () =>
  useAdminMutation<{ code: string; changes: UpdateBillerInput }, Awaited<ReturnType<typeof updateBiller>>>(
    ({ code, changes }, csrf) => updateBiller(code, changes, csrf),
  )

export const useSetGatewayLabel = () =>
  useAdminMutation<{ id: string; label: string }, Awaited<ReturnType<typeof setGatewayLabel>>>(
    ({ id, label }, csrf) => setGatewayLabel(id, label, csrf),
  )

export const useAssignGateway = () =>
  useAdminMutation<{ id: string; billerId: string }, Awaited<ReturnType<typeof assignGateway>>>(
    ({ id, billerId }, csrf) => assignGateway(id, billerId, csrf),
  )

/**
 * Export is a mutation, not a query: it is an explicit user action with a side
 * effect (a file lands on their disk), it must never be retried or cached, and
 * modelling it this way means the eventual move to an async job with a
 * download link changes this hook and nothing else.
 */
export function useExportTransactions(): UseMutationResult<string, Error, TransactionQuery> {
  return useMutation({
    mutationFn: async (query: TransactionQuery) => {
      const { blob, filename } = await exportTransactions(query)
      // The DOM dance lives here rather than in client.ts so the network layer
      // stays testable without a document.
      const url = URL.createObjectURL(blob)
      try {
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = filename
        document.body.append(anchor)
        anchor.click()
        anchor.remove()
      } finally {
        // Always revoked, even if the click throws — a leaked object URL pins
        // the whole blob in memory for the life of the document, and an export
        // is measured in megabytes.
        URL.revokeObjectURL(url)
      }
      return filename
    },
  })
}

/**
 * The field exposure policy (TRD 8002).
 *
 * `enabled` gates the fetch on the internal role rather than the screen simply
 * not being routed: a biller user who types the URL gets a 403 from the API
 * either way, but not issuing the request at all keeps a pointless denial out
 * of the audit trail.
 */
export const useFieldExposure = (enabled: boolean): UseQueryResult<FieldExposureResponse> =>
  useQuery({
    queryKey: apiKeys.fieldExposure(),
    queryFn: ({ signal }) => getFieldExposure(signal),
    enabled,
  })

/**
 * Granting or revoking changes what every biller can see, so BOTH mutations
 * invalidate the transaction cache as well as the policy itself. Without that,
 * an operator would grant a field and then look at a transaction list still
 * rendered from a cache built under the old policy — and conclude the grant had
 * not worked.
 */
function useFieldExposureMutation<TInput, TResult>(
  mutationFn: (input: TInput, csrfToken: string) => Promise<TResult>,
): UseMutationResult<TResult, Error, TInput> {
  const client = useQueryClient()
  const { csrfToken } = useAuthenticatedSession()
  return useMutation({
    mutationFn: (input: TInput) => mutationFn(input, csrfToken),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: apiKeys.fieldExposure() })
      await client.invalidateQueries({ queryKey: ['api', 'transactions'] })
    },
  })
}

export const useGrantFieldExposure = (): UseMutationResult<FieldExposureRule, Error, GrantFieldExposureInput> =>
  useFieldExposureMutation(grantFieldExposure)

export const useRevokeFieldExposure = (): UseMutationResult<{ revoked: string }, Error, string> =>
  useFieldExposureMutation(revokeFieldExposure)

/** Sending a support request changes nothing the portal displays, so unlike
 *  every other mutation here it invalidates no cache — the outcome lives in
 *  Maviance's inbox, not in any query. */
export const useSendSupportRequest = (): UseMutationResult<{ sent: boolean }, Error, SupportRequestInput> => {
  const { csrfToken } = useAuthenticatedSession()
  return useMutation({
    mutationFn: (input: SupportRequestInput) => sendSupportRequest(input, csrfToken),
  })
}

// ── beneficiaries (V1.1) ────────────────────────────────────────────────

/** The identities observed on one gateway, for the Beneficiaries panel. Keyed
 *  under the gateways prefix so every admin mutation invalidates it. */
export const useGatewayBeneficiaries = (gatewayId: string | null): UseQueryResult<GatewayBeneficiaries> =>
  useQuery({
    queryKey: ['api', 'gateways', gatewayId ?? '', 'beneficiaries'],
    queryFn: ({ signal }) => getGatewayBeneficiaries(gatewayId ?? '', signal),
    enabled: gatewayId !== null,
  })

/** The slices one organisation holds, for its edit form. Keyed under the
 *  billers prefix so a slice mutation's invalidation reaches it. */
export const useBillerSlices = (code: string | null): UseQueryResult<BillerSlices> =>
  useQuery({
    queryKey: ['api', 'billers', code ?? '', 'slices'],
    queryFn: ({ signal }) => getBillerSlices(code ?? '', signal),
    enabled: code !== null,
  })

export const useAssignSlice = () =>
  useAdminMutation<SliceInput, Awaited<ReturnType<typeof assignSlice>>>((input, csrf) => assignSlice(input, csrf))

export const useRevokeSlice = () =>
  useAdminMutation<SliceInput, Awaited<ReturnType<typeof revokeSlice>>>((input, csrf) => revokeSlice(input, csrf))
